import { Component, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, UntypedFormControl, FormArray, Validators } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { Observable ,  Subject } from 'rxjs';
import * as _ from 'lodash';

// data, models, helpers
import { descriptionSegmentData } from '../../segment-data/description-segment-data';
import { interchangesSegmentData } from '../../segment-data/interchanges-segment-data';
import { InterchangeModel } from '../../models/interchange.model';
import { segmentAnimation } from '../base/animations';
import { rowData, contentData, collapseWidthPx } from './table-data';
import { SegmentFieldItem } from 'utils/segment-field-item.interface';
import { Config } from 'shared/config/env.config';
import { languageLookup } from 'shared/product/product-lookups';
import { getFieldByMap } from 'utils/utils';

// components
import { BaseSegmentListComponent } from '../base/_base-segment-list.component';

// services
import { BrandService } from 'shared/brand/brand.service';
import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import { LookupsService } from 'shared/lookups/lookups.service';
import {mergeMap, takeWhile} from 'rxjs/operators';


@Component({
  moduleId: module.id,
  selector: 'interchanges-segment',
  templateUrl: 'interchanges.component.html',
  styleUrls: ['../base/_base-segment.component.scss'],
  animations: [segmentAnimation],
})
export class InterchangesComponent extends BaseSegmentListComponent implements OnDestroy {
  rowData: any[] = rowData;
  contentData: any[] = contentData;
  collapseWidth = collapseWidthPx;

  // define in the Child
  declare segmentForm: UntypedFormGroup;
  segmentName: string = 'interchanges'; // used in save request
  pathToScores: string = 'segments.interchanges.tallies';
  controlClassPrefix: string = 'interchanges-control-row-';

  segmentData: any = interchangesSegmentData;

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
    protected brandService: BrandService,
    protected fb: UntypedFormBuilder,
    protected sanitizer: DomSanitizer,
    private lookupsService: LookupsService,
  ) {
    super(ref, scorecardConnectorService, brandService, fb, sanitizer);
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnDestroy() {
    this.setApiLookup(this.getFieldByMap('subBrandId', this.segmentData), null);
    super.ngOnDestroy();
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public getSegmentFormData(): any {
    const kits = this.formArray.value;
    return _.map(kits, (kit: any) => {
      return _.pick(kit, [
        'brandId', 'brandLabel', 'subBrandId', 'subBrandLabel', 'interchangeNotes', 'internalNotes',
        'languageCode', 'partNumber', 'qualityGradeLevel', 'typeCode',
      ]);
    });
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onSelectTypeahead(event: any, fieldName: string, formGroup: UntypedFormGroup, index: number) {
    switch (fieldName) {
      case 'brandLabel':
        const brandId = _.get(event, 'item.option');
        _.set(this.itemDetailData[index], 'brand_AAIAID', brandId);
        _.set(this.itemDetailData[index], 'brand_label', _.get(event, 'item.title'));
        formGroup.get('brandId').setValue(brandId);
        // reset sub brand after brand select
        this.resetSubBrand(brandId, formGroup, index);
        this.updateControlsValidation();
        this.scorecardConnectorService.detectChangesInScorecard.emit();
        this.ref.markForCheck();
        break;
      default:
        break;
    }
  }

  onTypeaheadControlBlured(event: any, fieldName: string, formGroup: UntypedFormGroup, index: number) {
    if (fieldName === 'brandLabel') {
      // if typeahead wasn't selected - reset an old value
      if (_.get(this.itemDetailData[index], 'brand_label') !== formGroup.get('brandLabel').value) {
        formGroup.patchValue({
          'brandLabel': _.get(this.itemDetailData[index], 'brand_label'),
        });
        this.ref.markForCheck();
      }
    }
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  // replace parent method:
  protected setData(itemDetail: any = {}): void {
    if (this.itemDetailData) {
      this.segmentForm.setControl(this.segmentName, this.fb.array([]));
      // add data item to formArray
      _.forEach(this.itemDetailData, (item: any, index: number) => {
        this.formArray.push(this.createItemFG(item, index));
      });
      this.showSegment();
    }
  }

  // replace parent method:
  protected createItemFG(item: any, index?: number): UntypedFormGroup {
    return this.fb.group(this.createItemModel(item, index));
  }

  // replace parent method:
  protected createItemModel(item: any, index?: number): InterchangeModel {
    const model: any = new InterchangeModel();
    if (_.isNil(index)) { index = _.size(this.itemDetailData) - 1; } // in case of adding new row

    _.each(this.segmentData, (field: SegmentFieldItem) => {
      if (!field.field_map) { return; }

      // add typeahead to the model
      if (_.has(field, 'api_lookup.lookup')) {

        _.set(model, field.field_map, new UntypedFormControl(_.get(field.api_lookup, 'default_value', '')) );
        // const value = model[field.api_lookup.field_map].value;
        if (field.api_lookup.type === 'typeahead') {
          const dataSource = new Observable((observer: any) => {
              // Runs on every search
              observer.next(model[field.api_lookup.field_map].value);
            })
            .pipe(mergeMap((token: string) => {
              return this.lookupsService.fetch(field.api_lookup.lookup, token);
            }));
          _.set(model, `${field.api_lookup.field_map}_dataSource`, new UntypedFormControl(dataSource) );

        } else if (field.api_lookup.type === 'list') {
          this.lookupsService.fetch(field.api_lookup.lookup, model[field.api_lookup.field_map].value)
            .pipe(takeWhile(() => this.isActive))
            .subscribe((lookup: Array<any>) => {
              if (_.has(field.api_lookup, 'dataHandler')) {
                field.api_lookup.dataHandler(lookup);
              }
              this.setApiLookup(field, lookup, index);
            });
        }
      }

      const validators: any = this.getControlValidators(field);
      const value = _.get(item, field.item_name, '');
      _.set(model, field.field_map, new UntypedFormControl(value, validators));
    });
    return model;
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  /*
    * Reset sub brand after brand change
   */
  private resetSubBrand(brandId: string, formGroup: UntypedFormGroup, index: number): void {
    _.set(this.itemDetailData[index], 'sub_brand_AAIAID', '');
    _.set(this.itemDetailData[index], 'sub_brand_label', '');
    formGroup.get('subBrandId').setValue('');
    const item: SegmentFieldItem = this.getFieldByMap('subBrandId', this.segmentData);
    this.lookupsService.fetch(item.api_lookup.lookup, brandId)
    .pipe(takeWhile(() => this.isActive))
    .subscribe((lookup: Array<any>) => {
      if (_.has(item.api_lookup, 'dataHandler')) {
        item.api_lookup.dataHandler(lookup);
      }
      this.setApiLookup(item, lookup, index);
    });
  }

  private getInterchangeLookup(field: any, index: number): Array<any> {
    if (field.apiLookup) {
      return _.get(this.getFieldByMap('subBrandId', this.segmentData), `api_lookup.options[${index}]`);
    } else {
      return this.getFieldLookup(field.fieldName);
    }
  }

  private hasLookupData(field: any, index: number): boolean {
    return _.size(this.getInterchangeLookup(field, index)) > 0;
  }

  private setApiLookup(item: SegmentFieldItem, lookup: Array<any>, index?: number): void {
    if (!_.isNil(index)) {
      _.set(item, `api_lookup.options[${index}]`, lookup);
    } else {
      _.set(item, `api_lookup.options`, lookup);
    }
  }
}
